import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Box, Button, ButtonDropdown, CollectionPreferences, Header, Link, Select, SpaceBetween, Table } from '@cloudscape-design/components';
import BoardItem from '@cloudscape-design/board-components/board-item';
import { apiFetch } from '../../../auth/apiClient';
import { boardItemI18nStrings } from './common';
import BudgetProjection from './BudgetProjection';
import FinanceBudgetsHelp from '../../../helpPanelContents/financeBudgetsHelp';
import { downloadBudgetWorkbook } from '../budgetExportDownload';

const { currentFiscalYear } = require('../../../lib/budgetFiscal');

const money = value => value == null ? '—' : new Intl.NumberFormat('en-CA', {
  style: 'currency', currency: 'CAD', minimumFractionDigits: 2,
}).format(value);
const yearLabel = year => `${year.slice(0, 4)}/${year.slice(-2)}`;
const potCollator = new Intl.Collator('en-CA', { sensitivity: 'base', numeric: true });
const comparePots = (left, right) => {
  const national = pot => String(pot.name || '').trim().toLowerCase() === 'nwac national';
  return Number(national(left)) - Number(national(right))
    || potCollator.compare(left.name || '', right.name || '')
    || potCollator.compare(left.code || '', right.code || '')
    || potCollator.compare(left.id, right.id);
};

const preferenceKey = 'path.publishedBudgets.columns.v1';
const columns = [
  ['name', 'Budget Pot'], ['originalAllocation', 'Original Allocation'], ['adjustments', 'Adjustments'],
  ['allocation', 'Allocation'], ['committed', 'Committed'],
  ['paidRecorded', 'Paid (recorded)'], ['paidEstimated', 'Paid (estimated)'],
  ['outstandingRecorded', 'Outstanding (recorded)'], ['outstandingEstimated', 'Outstanding (estimated)'],
  ['remaining', 'Remaining'], ['projection', 'Projected Year End'], ['budgetUsedPercent', 'Budget used %'], ['budgetRemainingPercent', 'Budget remaining %'],
];
const defaults = new Set(['name', 'allocation', 'committed', 'remaining', 'budgetRemainingPercent']);
function normalizePreferences(saved = {}) {
  const known = new Set(columns.map(([id]) => id));
  const display = Array.isArray(saved.contentDisplay) ? saved.contentDisplay : [];
  const ids = [...new Set([...display.map(item => item?.id).filter(id => known.has(id)), ...known])];
  return {
    projectionVisibilityVersion: 1,
    contentDisplay: ids.map(id => ({ id, visible: id === 'name' || (display.find(item => item?.id === id)?.visible ?? defaults.has(id)) })),
    widths: Object.fromEntries(Object.entries(saved.widths || {}).filter(([id, width]) => known.has(id) && Number.isFinite(width) && width >= 80)),
  };
}
function loadPreferences() {
  try {
    const saved = JSON.parse(localStorage.getItem(preferenceKey) || '{}');
    const preferences = normalizePreferences(saved);
    // Apply the new opt-in default once, including browsers with the old visible default.
    // Preserve all other choices; subsequent explicit projection choices survive reloads.
    if (saved.projectionVisibilityVersion !== 1) {
      preferences.contentDisplay = preferences.contentDisplay.map(item => item.id === 'projection' ? { ...item, visible: false } : item);
    }
    return preferences;
  }
  catch (_) { return normalizePreferences(); }
}
const percent = value => value == null ? '—' : `${value.toLocaleString('en-CA', { maximumFractionDigits: 1 })}%`;

export default function PublishedBudgetWidget({ actions, toggleHelpPanel }) {
  const [preferences, setPreferences] = useState(loadPreferences);
  useEffect(() => {
    try { localStorage.setItem(preferenceKey, JSON.stringify(preferences)); } catch (_) { /* Storage may be unavailable. */ }
  }, [preferences]);
  const [year, setYear] = useState(() => {
    const requested = new URLSearchParams(window.location.search).get('fiscalYear');
    return /^\d{4}-\d{4}$/.test(requested || '') && Number(requested.slice(5)) === Number(requested.slice(0, 4)) + 1
      ? requested : currentFiscalYear();
  });
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [refresh, setRefresh] = useState(0);
  const [expanded, setExpanded] = useState([]);
  useEffect(() => {
    let cancelled = false;
    setLoading(true); setError(''); setData(null); setExpanded([]);
    (async () => {
      try {
        const response = await apiFetch(`/api/finance/fiscal-budgets?fiscalYear=${encodeURIComponent(year)}`);
        if (!response.ok) throw new Error(response.status === 409
          ? 'Fiscal budgets are being prepared. Please try again after the update.' : 'Unable to load budgets. Please try again.');
        const result = await response.json();
        if (!cancelled) { setData(result); setExpanded(result.pots.filter(pot => !pot.parentId)); }
      } catch (err) { if (!cancelled) setError(err.message); }
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [year, refresh]);
  const years = [...new Set([currentFiscalYear(), year, ...(data?.fiscalYears || [])])].sort().reverse();
  const options = years.map(value => ({ value, label: yearLabel(value) + (value === currentFiscalYear() ? ' (current)' : '') }));
  const { roots, children } = useMemo(() => {
    const pots = [...(data?.pots || []), ...(data?.unassignedFunding > 0 ? [{ id: 'unassigned', parentId: null, name: 'Unassigned pot', code: '', committed: data.unassignedFunding }] : [])];
    const ids = new Set(pots.map(p => p.id));
    const children = new Map();
    for (const pot of pots) {
      if (!children.has(pot.parentId)) children.set(pot.parentId, []);
      children.get(pot.parentId).push(pot);
    }
    for (const siblings of children.values()) siblings.sort(comparePots);
    return { roots: pots.filter(pot => !ids.has(pot.parentId)).sort(comparePots), children };
  }, [data]);
  const exportBudget = async () => {
    if (loading || exporting || !roots.length) return;
    setExporting(true); setError('');
    try {
      await downloadBudgetWorkbook({ fiscalYear: year, mode: 'published',
        columns: preferences.contentDisplay.filter(item => item.visible).map(({ id }) => ({
          id, label: columns.find(column => column[0] === id)[1],
        })),
      });
    } catch (err) { setError(err.message || 'Unable to export this budget. Please try again.'); }
    finally { setExporting(false); }
  };
  return <BoardItem i18nStrings={boardItemI18nStrings}
    header={<Header variant="h2"
        info={<Link variant="info" onFollow={event => {
          event.preventDefault();
          toggleHelpPanel?.(<FinanceBudgetsHelp />, 'Budgets', FinanceBudgetsHelp.aiContext);
        }}>Info</Link>}
        actions={<SpaceBetween direction="horizontal" size="xs">
          <Select ariaLabel="Fiscal year" options={options} disabled={exporting}
            selectedOption={options.find(option => option.value === year)}
            onChange={({ detail }) => setYear(detail.selectedOption.value)} />
          <Button iconName="refresh" disabled={loading || exporting} onClick={() => setRefresh(value => value + 1)}>Refresh</Button>
          <Button iconName="download" loading={exporting} disabled={loading || exporting || !roots.length} onClick={exportBudget}>Export</Button>
        </SpaceBetween>}>Budget {yearLabel(year)}</Header>}
    settings={<ButtonDropdown ariaLabel="Budget widget settings" variant="icon"
      items={[{ id: 'remove', text: 'Remove widget' }]}
      onItemClick={({ detail }) => { if (detail.id === 'remove') actions.removeItem(); }} />}
  >
    <SpaceBetween size="m">
    {error && <Alert type="error">{error}</Alert>}
    {!!(data?.unassignedOccurrences || data?.unresolvedFundingChoices || data?.unassignedSalary || data?.salaryIssues?.length || data?.unassignedExpense || data?.expenseIssues?.length) && <Alert type="warning">Some entries need a valid budget assignment. Unassigned amounts are shown separately from regional pots.</Alert>}
    {!!data?.unmatchedPaidLines && <Alert type="warning">Some recorded payments could not be matched to approved funding. Paid and outstanding breakdowns may be incomplete.</Alert>}
    <Table variant="embedded" trackBy="id" loading={loading} loadingText="Loading budgets" items={roots}
      resizableColumns stickyHeader enableKeyboardNavigation
      ariaLabels={{ tableLabel: 'Budgets', expandButtonLabel: pot => `Expand ${pot.name}`,
        collapseButtonLabel: pot => `Collapse ${pot.name}`, resizerRoleDescription: 'Column resize handle' }}
      columnDefinitions={preferences.contentDisplay.filter(item => item.visible).map(({ id }) => ({
        id, header: columns.find(column => column[0] === id)[1],
        width: preferences.widths[id] || (id === 'name' ? 340 : id === 'projection' ? 225 : 185), minWidth: id === 'name' ? 220 : id === 'projection' ? 180 : 130,
        cell: pot => id === 'name'
          ? <><Box fontWeight="bold">{pot.name}</Box><Box color="text-body-secondary" fontSize="body-s">{pot.code}</Box></>
          : id === 'projection' ? pot.id === 'unassigned' ? <Box>—</Box> : <BudgetProjection projection={pot.projection} potName={pot.name} />
          : <Box textAlign="right">{id.endsWith('Percent') ? percent(pot[id]) : money(pot[id])}</Box>,
      }))}
      onColumnWidthsChange={({ detail }) => {
        const visible = preferences.contentDisplay.filter(item => item.visible);
        setPreferences(previous => normalizePreferences({ ...previous, widths: {
          ...previous.widths, ...Object.fromEntries(detail.widths.map((width, index) => [visible[index].id, width])),
        } }));
      }}
      preferences={<CollectionPreferences title="Table preferences" confirmLabel="Confirm" cancelLabel="Cancel"
        preferences={preferences} onConfirm={({ detail }) => setPreferences(normalizePreferences({ ...preferences, ...detail }))}
        contentDisplayPreference={{ title: 'Columns', description: 'Choose the details to display and their order.',
          options: columns.map(([id, label]) => ({ id, label, alwaysVisible: id === 'name' })) }} />}
      expandableRows={{ getItemChildren: pot => children.get(pot.id) || [],
        isItemExpandable: pot => !!children.get(pot.id)?.length, expandedItems: expanded,
        onExpandableItemToggle: ({ detail }) => setExpanded(items => detail.expanded
          ? [...items, detail.item] : items.filter(item => item.id !== detail.item.id)) }}
      empty={<Box padding="m">No published budget for this fiscal year.</Box>} />
    </SpaceBetween>
  </BoardItem>;
}
